'use client';
import * as React from 'react';
import * as Yup from 'yup';

import { Formik, Form } from 'formik';
import { Input, PhoneInput } from '@components/Form';

import { identify } from '@repo/ui/src/utils/identify';
import { captureMessage } from '@sentry/nextjs';

import '@utils/forms/phoneValidation';
import Button from '@repo/ui/src/components/Button';
import { useEmailSignup } from '@hooks/customer/useEmailSignup';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MarketingSignupProps } from '@typings/marketingSignup';
import { ConnectedFocusError } from 'focus-formik-error';

export interface Props {
  darkMode?: boolean;
  emailSignup?: MarketingSignupProps;
  smsSignup?: MarketingSignupProps;
  submitButton: string;
  sourceDetail?: string;
  success?: string;
  failure?: string;
  fullWidth?: boolean;
  center?: boolean;
}

const MarketingCaptureForm = ({
  emailSignup,
  smsSignup,
  darkMode,
  submitButton,
  success,
  failure,
  sourceDetail,
  fullWidth,
  center,
}: Props) => {
  const { trigger, isMutating: isSubmitting, data, error } = useEmailSignup();
  const submitted = !!data;
  const buttonText = submitButton || 'Submit';
  const validationSchema = Yup.object().shape({
    email: Yup.string().required().email(emailSignup?.failure),
    sms: Yup.string().phone(smsSignup?.failure),
  });
  return (
    <div className={classNames(fullWidth && 'tw-w-full')}>
      <Formik
        initialValues={{ email: '', sms: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values: { email: string; sms: string }) => {
          try {
            const emailSubscribe = values?.email ? !!values?.email : undefined;
            const phoneNumber = values?.sms;
            const smsSubscribe = values?.sms ? !!values?.sms : undefined;

            await trigger({
              email: values?.email,
              phoneNumber,
              smsSubscribe,
              sourceDetail,
              emailSubscribe,
            });
            identify({ email: values?.email });
          } catch (e) {
            captureMessage('track marketing form failed to submit', {
              level: 'error',
              user: { email: values.email },
            });
          }
        }}
      >
        {() => {
          return (
            <Form className={classNames('tw-grid tw-gap-y-8', darkMode && 'tw-dark')} role="form">
              <ConnectedFocusError />

              {emailSignup && (
                <Input
                  label={emailSignup?.label}
                  required={emailSignup?.required}
                  placeholder={emailSignup?.placeholder}
                  disabled={isSubmitting || submitted}
                  type="email"
                  name="email"
                  id={`email_submit_${sourceDetail}`}
                  autoComplete="email"
                  data-testid="marketing-capture-email"
                />
              )}
              {smsSignup && (
                <PhoneInput
                  id="sms"
                  name="sms"
                  type="text"
                  disabled={isSubmitting || submitted}
                  placeholder={smsSignup?.placeholder}
                  label={smsSignup?.label}
                  required={smsSignup?.required}
                  autoComplete="tel"
                />
              )}
              {error && (
                <p
                  className={classNames(darkMode ? 'tw-text-surface-200' : 'tw-text-text-500')}
                  id={failure}
                >
                  {failure}
                </p>
              )}
              {submitted && (
                <p className={classNames(darkMode ? 'tw-text-surface-200' : 'tw-text-text-500')}>
                  {success}
                </p>
              )}
              <Button
                disabled={isSubmitting || (!error && submitted)}
                type="submit"
                aria-label="submit"
                className={classNames(center && 'tw-mx-auto tw-max-w-[200px]')}
                data-testid="marketing-capture-submit"
              >
                {buttonText}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default MarketingCaptureForm;
